// PeerHost — the coordinator that turns a browser into a P2P host (docs/p2p.md).
//
// It owns a LocalServer (running the SERVER halves, local-server.ts) and the set
// of clients connected to it, and is the ONE slot-aware sink router between them:
//
//   joiner → host : a peer's first reliable message is the connect handoff, so
//                   it allocates the peer's slot (LocalServer.connectPeer);
//                   every later datagram is fed in as wire bytes (feedFrom).
//   host → client : the LocalServer's per-slot sink routes each recipient's
//                   coalesced datagram to that slot's client — a remote peer
//                   sends it over its reliable/unreliable channel, a local tile's
//                   loopback loops it back in-process. Interest-management leaves
//                   become peer_left, exactly as the loopback and hosted server do.
//
// To the LocalServer a local player (a split-screen tile's loopback) and a remote
// peer are already just slots, so both are HostedClients in ONE `slot → client`
// map and `routeSink` is `clients.get(slot)?.deliver(…)`. The host's own client
// (its loopback on slot 0) rides in via opts.localClient; additional local tiles
// come in via attachLocal on allocated slots (docs/p2p.md §split-screen).

import { LocalServer } from "./local-server.js";
import { RtcHostPeer, type RtcHostPeerDeps } from "./rtc-host-peer.js";

// A joiner that opens a peer but never advances the handshake — never sends the
// connect handoff to take a slot, and never sends a session-info request —
// would otherwise pin an RTCPeerConnection forever (until it takes a slot it
// isn't in `peers`, so close() can't reap it). Reap it after this long. This is
// the browser mirror of the server's handshake watchdog (C2, ws-server.ts).
const HANDSHAKE_TIMEOUT_MS = 15_000;
// A session-info peer has served its purpose once answered — it never becomes a
// game client. Give the reply a moment to flush over the channel, then close it
// rather than waiting out the full handshake timeout or leaking it.
const INFO_REPLY_LINGER_MS = 2_000;

// A joiner's session-info request — the one text control frame a peer sends
// before it becomes a game client (session-info.ts). Anything else is ignored.
function isInfoRequest(data: string): boolean {
  try {
    const m = JSON.parse(data) as { t?: unknown };
    return !!m && m.t === "info";
  } catch { return false; }
}

// The session metadata a joiner's bootstrap asks the host for (session-info.ts):
// how to connect, which mods to run, and the ROM hash it must match. Read live at
// request time.
export interface HostSessionInfo {
  // How the joiner reaches the game the inviter is playing:
  //   "p2p"    — the inviter's browser IS the server; connect the mods P2P to it
  //              (the modUrls are the client-half origins to fetch and run).
  //   "server" — the inviter is a plain client of a shared server; connect to the
  //              SAME server instead (modUrls is [deployment-root URL], so the
  //              friend enters the same game and the server routes them fresh).
  mode: "p2p" | "server";
  // The mod URLs the joiner loads. In "p2p" mode: the host's running client-half
  // origins. In "server" mode: the current game's stable deployment-root URL.
  modUrls: string[];
  // The SHA-1 hash of the inviter's loaded ROM, so the joiner loads the SAME game
  // and can't accidentally pick an incompatible one. null when the inviter hasn't
  // loaded a ROM yet (the joiner then falls back to an unconstrained pick).
  romHash: string | null;
}

// Serialize a session-info reply (the host→joiner `{t:"mods"}` frame) from the
// live metadata. One shape for both hosting modes; the joiner branches on `mode`.
function encodeSessionInfoReply(info: HostSessionInfo): string {
  return JSON.stringify({ t: "mods", mode: info.mode, urls: info.modUrls, romHash: info.romHash });
}

// Answer a joiner's session-info bootstrap over a throwaway broker-relayed peer,
// WITHOUT hosting any game. This is the "server invite" host side: a plain client
// of a shared server has no in-page LocalServer to run (unlike a P2P host), but
// can still tell a friend which game + ROM it's playing so they join the same
// game ON THE SERVER. The peer only ever sends {t:"info"} and is torn down after
// the reply — it never becomes a game client, so there's nothing else to route.
export async function acceptInfoOffer(
  offerSdp: string,
  sessionInfo: () => HostSessionInfo,
  peerDeps?: RtcHostPeerDeps,
): Promise<string> {
  const { answerSdp, peer } = await RtcHostPeer.accept(offerSdp, peerDeps);
  // Reap the peer if it never asks (or never opens its channel) — nothing else
  // owns it in server-invite mode (there's no PeerHost/LocalServer here).
  let watchdog: ReturnType<typeof setTimeout> | null =
    setTimeout(() => { watchdog = null; peer.close(); }, HANDSHAKE_TIMEOUT_MS);
  const disarm = (): void => { if (watchdog !== null) { clearTimeout(watchdog); watchdog = null; } };
  peer.onClose(disarm);
  peer.onMessage((data) => {
    if (typeof data === "string" && isInfoRequest(data)) {
      peer.send(encodeSessionInfoReply(sessionInfo()));
      // Served — flush the reply, then tear the throwaway peer down (it never
      // becomes a game client) instead of leaking it until the watchdog fires.
      disarm();
      setTimeout(() => peer.close(), INFO_REPLY_LINGER_MS);
    }
    // Anything else (there shouldn't be any) is ignored: no server to feed.
  });
  return answerSdp;
}

// A client the LocalServer delivers to, addressed by slot. Both a remote peer
// (RtcHostPeer, over WebRTC) and a local tile (LoopbackBusSocket, in-process)
// implement it, so the per-slot sink router treats them uniformly.
export interface HostedClient {
  // A coalesced server→client datagram for this client. `reliable` selects the
  // transport's class (a peer routes the unreliable class to its unordered
  // channel; a loopback ignores it — there is no wire).
  deliver(bytes: Uint8Array, reliable: boolean): void;
  // An interest-management leave: `subject` left this client's interest set.
  deliverPeerLeft(subject: number): void;
}

export interface PeerHostOptions {
  // Injected into every RtcHostPeer.accept (createPeerConnection / iceServers).
  // Tests pass node-datachannel's polyfill; production uses the DOM defaults.
  peerDeps?: RtcHostPeerDeps;
  // The host's OWN local client (its loopback), routed by the same per-slot sink
  // so the loopback and the peers share one router. It already holds slot 0 (it
  // connected during singleplayer bring-up), so it's registered there. Omitted
  // in a peers-only host (and in tests that drive the server directly).
  localClient?: HostedClient;
  // The session metadata for a joiner's bootstrap (session-info.ts): a joiner
  // asks over a throwaway peer BEFORE it fetches the client halves, since the
  // short share link (?join=) carries no mod list or ROM identity. Read at
  // request time so it always reflects the current game. Omitted where info
  // isn't exercised (a host that never advertises, or a direct-drive test).
  sessionInfo?: () => HostSessionInfo;
}

export class PeerHost {
  // slot → the client that owns it (a remote peer or a local loopback). The
  // one routing table — `routeSink` is a single lookup into it.
  private readonly clients = new Map<number, HostedClient>();
  // The subset of those slots that are REMOTE peers, kept typed so their
  // connection lifecycle (peerCount, close) is addressable without downcasting.
  // Local clients (the host's own loopback, split-screen tiles) are excluded.
  private readonly peers = new Map<number, RtcHostPeer>();
  // Peers accepted but not yet slotted (still mid-handshake, or a throwaway
  // session-info peer). They aren't in `peers` yet, so close() would otherwise
  // miss them; tracking them here lets a host teardown reap in-flight peers.
  private readonly pendingPeers = new Set<RtcHostPeer>();
  // Set once close() has run. acceptOffer awaits ICE gathering (up to a few
  // seconds) BEFORE the peer can be tracked, so a close() during that window
  // can't reap the not-yet-created peer; the flag lets acceptOffer detect it
  // resolved into a dead host and close the orphan itself.
  private closed = false;

  constructor(
    private readonly server: LocalServer,
    private readonly opts: PeerHostOptions = {},
  ) {
    server.setSink(
      (toSlot, datagram, reliable) => this.routeSink(toSlot, datagram, reliable),
      (toSlot, subject) => this.routePeerLeft(toSlot, subject),
    );
    // The host's own client already holds slot 0 (its loopback connected during
    // singleplayer bring-up); route that slot back to it.
    if (opts.localClient) this.clients.set(0, opts.localClient);
  }

  // Accept a joiner's offer, wire the resulting peer into the server, and return
  // the answer SDP for the broker to relay back. The peer occupies a slot from
  // its first (handoff) message until it disconnects.
  async acceptOffer(offerSdp: string): Promise<string> {
    const { answerSdp, peer } = await RtcHostPeer.accept(offerSdp, this.opts.peerDeps);
    // The host was torn down while we awaited ICE gathering: close() already
    // reaped every tracked peer, and this one was created afterward, so nothing
    // else will ever close it. Close the orphan now rather than leak its
    // RTCPeerConnection + data channels. (We still return the answer; the joiner
    // simply never connects to a dead host.)
    if (this.closed) {
      peer.close();
      return answerSdp;
    }

    let slot = -1;
    // Track + watchdog the peer until it either takes a slot (→ reaped by
    // close()/onClose) or is torn down. Without this a peer that never sends
    // its handoff pins an RTCPeerConnection forever.
    this.pendingPeers.add(peer);
    let watchdog: ReturnType<typeof setTimeout> | null =
      setTimeout(() => { watchdog = null; if (slot < 0) peer.close(); }, HANDSHAKE_TIMEOUT_MS);
    const disarm = (): void => { if (watchdog !== null) { clearTimeout(watchdog); watchdog = null; } };

    peer.onMessage((data) => {
      // A TEXT frame from a joiner is a control message, not a game frame. The
      // only one today is the session-info request (session-info.ts): answer it
      // with the hosted mod URLs over the same reliable channel and take NO slot
      // — a bootstrap peer isn't a game client and is torn down after the reply.
      if (typeof data === "string") {
        if (isInfoRequest(data)) {
          const info = this.opts.sessionInfo?.() ?? { mode: "p2p" as const, modUrls: [], romHash: null };
          peer.send(encodeSessionInfoReply(info));
          // Served — flush the reply, then close (it never becomes a game
          // client). The slot guard defends the odd case of a later handoff.
          disarm();
          setTimeout(() => { if (slot < 0) peer.close(); }, INFO_REPLY_LINGER_MS);
        }
        return;
      }
      if (slot < 0) {
        // First message is the connect handoff — allocate this peer's slot.
        slot = this.server.connectPeer(data);
        if (slot >= 0) {
          this.clients.set(slot, peer);
          this.peers.set(slot, peer);
          this.pendingPeers.delete(peer);
          disarm(); // it's a real client now; close() reaps it via `peers`
        } else {
          peer.close(); // server full — drop the peer cleanly
        }
        return;
      }
      this.server.feedFrom(slot, data);
    });
    peer.onClose(() => {
      disarm();
      this.pendingPeers.delete(peer);
      if (slot < 0) return;
      this.clients.delete(slot);
      this.peers.delete(slot);
      this.server.disconnectSlot(slot);
      slot = -1;
    });

    return answerSdp;
  }

  // Register a local in-process client (a split-screen tile's loopback) on a
  // freshly allocated slot, connecting it to the server. Returns the slot, or -1
  // when the server is full. The host's own slot-0 client comes in via
  // opts.localClient instead (it self-connects during singleplayer bring-up); a
  // split-screen tile that joins later allocates its slot here — the local
  // counterpart of a peer's handoff-driven connectPeer.
  attachLocal(client: HostedClient, handoff: Uint8Array = new Uint8Array(0)): number {
    const slot = this.server.allocSlot();
    if (slot < 0) return -1;
    // Register BEFORE connecting so a member's connect-time send to this slot
    // already finds its client (a loopback's ownSink path is sink-ready before
    // connect too — this keeps the managed path's guarantee identical).
    this.clients.set(slot, client);
    this.server.connectSlot(slot, handoff);
    return slot;
  }

  // Detach a local client and free its slot (fires disconnect across members).
  // The inverse of attachLocal; a remote peer frees its slot via onClose instead.
  detachLocal(slot: number): void {
    if (!this.clients.delete(slot)) return;
    this.server.disconnectSlot(slot);
  }

  // Number of connected remote peers (excludes the host's own local client and
  // any split-screen tiles — those are local, not peers).
  get peerCount(): number {
    return this.peers.size;
  }

  // Peers accepted but not yet slotted (mid-handshake / throwaway info peers).
  // Exposed for diagnostics and tests — these are the ones the handshake
  // watchdog and close() reap so they can't leak RTCPeerConnections.
  get pendingPeerCount(): number {
    return this.pendingPeers.size;
  }

  // Tear down every remote peer connection. Local clients (loopbacks) are owned
  // by the emulator host and outlive a share session, so they're left connected;
  // the LocalServer itself is owned by the caller and is not stopped here.
  close(): void {
    this.closed = true;
    for (const peer of this.peers.values()) peer.close();
    for (const slot of this.peers.keys()) this.clients.delete(slot);
    this.peers.clear();
    // Reap peers still mid-handshake (never slotted) too — their onClose
    // handlers remove them from the set as they close.
    for (const peer of [...this.pendingPeers]) peer.close();
    this.pendingPeers.clear();
  }

  private routeSink(toSlot: number, datagram: Uint8Array, reliable: boolean): void {
    this.clients.get(toSlot)?.deliver(datagram, reliable);
  }

  private routePeerLeft(toSlot: number, subject: number): void {
    this.clients.get(toSlot)?.deliverPeerLeft(subject);
  }
}
